import { ArrowLeft, BookOpen, ChevronDown, SearchX, ThumbsDown, ThumbsUp, X } from "lucide-react";
import { useEffect, useId, useRef, useState, type RefObject } from "react";

import { Button, cx, EmptyState, ErrorAlert, Input, Sheet, Spinner } from "@/components/ui";
import { useHelpArticle, useHelpArticles, useHelpContext, useHelpFeedback } from "@/lib/help-queries";
import { readRecentHelp, rememberHelpArticle } from "@/lib/help-recent";

import { HelpMarkdown } from "./HelpMarkdown";

interface Target {
  slug?: string;
  key?: string;
}

type View = { kind: "list" } | { kind: "article"; slug?: string; key?: string };

const SEARCH_DELAY_MS = 250;

const ITEM =
  "flex w-full flex-col items-start gap-0.5 rounded-lg border border-border bg-surface px-3.5 py-3 text-left transition-colors hover:bg-muted";

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

function Header({
  titleId,
  title,
  onBack,
  onClose,
  closeRef,
  headingRef,
}: {
  titleId: string;
  title: string;
  onBack?: () => void;
  onClose: () => void;
  closeRef: RefObject<HTMLButtonElement | null>;
  headingRef: RefObject<HTMLHeadingElement | null>;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-start justify-between gap-3">
        {onBack ? (
          <Button
            variant="ghost"
            size="sm"
            icon={<ArrowLeft className="size-4" aria-hidden />}
            onClick={onBack}
            className="-ml-2"
          >
            Back
          </Button>
        ) : (
          <span />
        )}
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          className="rounded p-1 text-fg-subtle hover:bg-muted"
          aria-label="Close help"
        >
          <X className="size-4" aria-hidden />
        </button>
      </div>
      <h2
        id={titleId}
        ref={headingRef}
        tabIndex={-1}
        className="text-lg font-bold text-fg focus:outline-none"
      >
        {title}
      </h2>
    </div>
  );
}

interface ViewProps {
  titleId: string;
  onClose: () => void;
  closeRef: RefObject<HTMLButtonElement | null>;
  headingRef: RefObject<HTMLHeadingElement | null>;
}

/** The articles this browser opened last, in a section that starts closed so the full list stays in front. */
function RecentlyViewed({
  articles,
  onOpen,
}: {
  articles: { slug: string; title: string }[];
  onOpen: (slug: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const listId = useId();
  return (
    <section className="rounded-lg border border-border">
      <h3 className="text-sm font-semibold text-fg">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center justify-between gap-2 rounded-lg px-3.5 py-2.5 text-left hover:bg-muted"
        >
          <span>
            Recently viewed <span className="font-normal text-fg-muted">({articles.length})</span>
          </span>
          <ChevronDown
            className={cx("size-4 text-fg-subtle transition-transform", open && "rotate-180")}
            aria-hidden
          />
        </button>
      </h3>
      {open && (
        <ul id={listId} aria-label="Recently viewed articles" className="flex flex-col gap-1.5 px-2 pb-2">
          {articles.map((a) => (
            <li key={a.slug}>
              <button type="button" onClick={() => onOpen(a.slug)} className={cx(ITEM, "py-2 text-sm")}>
                {a.title}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ListView({
  q,
  setQ,
  category,
  setCategory,
  onOpen,
  ...header
}: ViewProps & {
  q: string;
  setQ: (q: string) => void;
  category: string | undefined;
  setCategory: (category: string | undefined) => void;
  onOpen: (slug: string) => void;
}) {
  const debounced = useDebounced(q, SEARCH_DELAY_MS);
  const list = useHelpArticles(debounced, category);
  const [recent] = useState(readRecentHelp);
  const articles = list.data?.articles ?? [];
  const categories = list.data?.categories ?? [];
  const searching = Boolean(q.trim() || category);
  // Only slugs in the loaded list: an article this person may no longer see is never offered.
  const recentArticles = searching ? [] : recent.flatMap((slug) => articles.filter((a) => a.slug === slug));
  const searchId = useId();

  return (
    <>
      <Header {...header} title="Help" />
      <div className="flex flex-col gap-1.5">
        <label htmlFor={searchId} className="sr-only">
          Search help
        </label>
        <Input
          id={searchId}
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search help"
          autoComplete="off"
          maxLength={200}
        />
      </div>

      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Category">
          {[undefined, ...categories].map((c) => {
            const pressed = c === category;
            return (
              <button
                key={c ?? "all"}
                type="button"
                aria-pressed={pressed}
                onClick={() => setCategory(c)}
                className={cx(
                  "h-8 rounded-full border px-3 text-sm font-medium transition-colors",
                  pressed
                    ? "border-brand-500 bg-brand-50 text-brand-700"
                    : "border-border bg-surface text-fg-muted hover:bg-muted",
                )}
              >
                {c ?? "All"}
              </button>
            );
          })}
        </div>
      )}

      {list.isError ? (
        <div className="flex flex-col items-start gap-3">
          <ErrorAlert error={list.error} />
          <Button variant="secondary" size="sm" onClick={() => void list.refetch()}>
            Try again
          </Button>
        </div>
      ) : list.isPending ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : (
        <>
          {recentArticles.length > 0 && <RecentlyViewed articles={recentArticles} onOpen={onOpen} />}
          {articles.length === 0 ? (
            <EmptyState
              icon={<SearchX className="size-5" aria-hidden />}
              title="No articles match"
              description="Try a different word, or clear the search and the category."
            />
          ) : (
            <ul className="flex flex-col gap-2" aria-label="Help articles">
              {articles.map((a) => (
                <li key={a.slug}>
                  <button type="button" onClick={() => onOpen(a.slug)} className={ITEM}>
                    <span className="text-sm font-semibold text-fg">{a.title}</span>
                    <span className="text-[13px] text-fg-muted">{a.summary}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </>
  );
}

function Feedback({ slug }: { slug: string }) {
  const feedback = useHelpFeedback();
  const [vote, setVote] = useState<boolean | null>(null);
  const send = (helpful: boolean) =>
    feedback.mutate({ slug, helpful }, { onSuccess: () => setVote(helpful) });
  const choice = (helpful: boolean, label: string) => (
    <Button
      variant="secondary"
      size="sm"
      aria-pressed={vote === helpful}
      disabled={feedback.isPending}
      icon={
        helpful ? <ThumbsUp className="size-4" aria-hidden /> : <ThumbsDown className="size-4" aria-hidden />
      }
      className={cx(vote === helpful && "border-brand-500 bg-brand-50 text-brand-700")}
      onClick={() => send(helpful)}
    >
      {label}
    </Button>
  );
  return (
    <div className="flex flex-col gap-3 border-t border-border pt-4">
      <p className="text-sm font-semibold text-fg">Was this helpful?</p>
      <div className="flex gap-2">
        {choice(true, "Yes")}
        {choice(false, "No")}
      </div>
      {feedback.isError && <ErrorAlert error={feedback.error} />}
      {vote !== null && !feedback.isError && (
        <p role="status" className="text-[13px] text-fg-muted">
          Thanks for the feedback
        </p>
      )}
    </div>
  );
}

function ArticleView({
  slug,
  helpKey,
  onBrowse,
  onNavigate,
  ...header
}: ViewProps & { slug?: string; helpKey?: string; onBrowse: () => void; onNavigate: () => void }) {
  const bySlug = useHelpArticle(slug);
  const byKey = useHelpContext(slug ? undefined : helpKey);
  const query = slug ? bySlug : byKey;
  const article = query.data ?? null;
  const shown = article?.slug;

  useEffect(() => {
    if (shown) rememberHelpArticle(shown);
  }, [shown]);

  return (
    <>
      <Header {...header} title={article?.title ?? "Help"} onBack={onBrowse} />
      {query.isError ? (
        <div className="flex flex-col items-start gap-3">
          <ErrorAlert error={query.error} />
          <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
            Try again
          </Button>
        </div>
      ) : query.isPending ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : article ? (
        <>
          {article.summary && <p className="text-[13.5px] text-fg-muted">{article.summary}</p>}
          <HelpMarkdown body={article.body_md} title={article.title} onNavigate={onNavigate} />
          <Feedback key={article.slug} slug={article.slug} />
        </>
      ) : (
        <EmptyState
          icon={<BookOpen className="size-5" aria-hidden />}
          title="We don't have an article for this yet"
          description="You can look through the help articles instead."
          action={
            <Button variant="secondary" onClick={onBrowse}>
              Browse help
            </Button>
          }
        />
      )}
    </>
  );
}

/** The help centre as a dialog: the articles to search and read, or the one article for a help key. */
export function HelpPanel({ target, onClose }: { target: Target; onClose: () => void }) {
  const titleId = `help-title-${useId()}`;
  const closeRef = useRef<HTMLButtonElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [view, setView] = useState<View>(
    target.slug || target.key ? { kind: "article", slug: target.slug, key: target.key } : { kind: "list" },
  );
  const [q, setQ] = useState("");
  const [category, setCategory] = useState<string | undefined>();
  const focusedView = useRef<View | null>(null);

  // Focus lands on the close button when the panel opens, then on the new heading as the view changes.
  // The view it last focused for is remembered, so StrictMode's second run of the effect does nothing.
  useEffect(() => {
    if (focusedView.current === view) return;
    const opening = focusedView.current === null;
    focusedView.current = view;
    (opening ? closeRef : headingRef).current?.focus();
  }, [view]);

  const header = { titleId, onClose, closeRef, headingRef };
  return (
    <Sheet
      onClose={onClose}
      labelledBy={titleId}
      className="max-h-[85vh] max-w-[560px] rounded-[18px] shadow-[0_8px_24px_rgba(51,31,20,0.12)]"
    >
      <div className="flex flex-col gap-3.5 p-[22px]">
        {view.kind === "list" ? (
          <ListView
            {...header}
            q={q}
            setQ={setQ}
            category={category}
            setCategory={setCategory}
            onOpen={(slug) => setView({ kind: "article", slug })}
          />
        ) : (
          <ArticleView
            {...header}
            slug={view.slug}
            helpKey={view.key}
            onBrowse={() => setView({ kind: "list" })}
            onNavigate={onClose}
          />
        )}
      </div>
    </Sheet>
  );
}
